// Import-to-Connect modal (Solution Studio Build tab). Pushes the project's CONNECT_IMPORT
// projection (the Design Model's in-Connect operational resources — hours, queues, routing profiles,
// agent status, quick connects, user hierarchies) straight into a target Amazon Connect instance by
// REUSING the AWS platform's existing import pipeline unchanged:
//   listExistingResources → buildNameToIdMaps → ImportValidationService → importResources
// The only Solution-Studio-specific step is the data source: instead of parsing an uploaded Excel DDD,
// we fetch the build phase's CONNECT_IMPORT artifact (already shaped exactly like the importer's
// parsedData) via its presigned URL. Contact flows and security profiles are intentionally NOT imported
// here (the import tool doesn't support them yet); flows ship as the Build tab .zip download.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal, Stack, Group, Title, Text, Select, Button, Alert, Badge, Table, Checkbox, Radio,
  Loader, Tooltip, Divider,
} from '@mantine/core';
import {
  IconUpload, IconAlertCircle, IconCheck, IconEye, IconInfoCircle, IconCloudUpload,
} from '@tabler/icons-react';

import { useAwsCredentials } from '../../../../shared/contexts/CredentialsContext';
import { listConnectInstances } from '../../aws/connectClient';
import ImportValidationService from '../../import/importValidationService';
import { listExistingResources, buildNameToIdMaps } from '../../import/connectCreateClient';
import { importResources } from '../../import/resourceImportDispatcher';
import { buildUserHierarchyStructure } from '../../import/reverseDataMappers';
import ImportLogService from '../../import/importLogService';
import {
  IMPORT_STATUS, CONFLICT_RESOLUTION, RESOURCE_TYPE_LABELS, IMPORTABLE_RESOURCE_TYPES, ERROR_MESSAGES,
} from '../../import/importConstants';
import ImportPreviewModal from '../../components/ImportPreviewModal';
import ImportProgressPanel from '../../components/ImportProgressPanel';
import ImportLogViewer from '../../components/ImportLogViewer';
import AWSCredentialChangeSection from '../../components/AWSCredentialChangeSection';
import { getArtifactUrl, recordImportResults, getProject } from '../api_scripts/solnStudioClient';

// Map the import dispatcher's resourceType → the nameToIdMaps sub-map that holds existing-resource ids,
// so SKIPPED (already-existing) resources can also be recorded in the ledger, not just created/updated.
const NAME_TO_ID_KEY = {
  queues: 'queueNameToId',
  hoursOfOperation: 'hoursOfOperationNameToId',
  routingProfiles: 'routingProfileNameToId',
  agentStatus: 'agentStatusNameToId',
  quickConnects: 'quickConnectNameToId',
  userHierarchies: 'hierarchyGroupNameToId',
};

// Combine created/updated (results.success carry id) + skipped-existing (id from nameToIdMaps) into the
// flat { resourceType, resourceName, id } list the import ledger records.
function ledgerResults(results, nameToIdMaps) {
  const out = (results.success || []).map((r) => ({ resourceType: r.resourceType, resourceName: r.resourceName, id: r.id }));
  for (const s of results.skipped || []) {
    const id = nameToIdMaps?.[NAME_TO_ID_KEY[s.resourceType]]?.[s.resourceName];
    if (id) out.push({ resourceType: s.resourceType, resourceName: s.resourceName, id });
  }
  return out.filter((r) => r.id);
}

function SolnStudioImportModal({ opened, onClose, project, getApiToken, onImported }) {
  const { awsCredentials, setAwsCredentials } = useAwsCredentials();

  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [loadingInstances, setLoadingInstances] = useState(false);

  const [parsedData, setParsedData] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [validationSummary, setValidationSummary] = useState(null);
  const [existingResources, setExistingResources] = useState(null);
  const [nameToIdMaps, setNameToIdMaps] = useState(null);

  const [selectedResourceTypes, setSelectedResourceTypes] = useState({});
  const [conflictResolution, setConflictResolution] = useState(CONFLICT_RESOLUTION.SKIP);
  const [perResourceConflicts, setPerResourceConflicts] = useState({});

  const [importProgress, setImportProgress] = useState({});
  const [importLog, setImportLog] = useState(null);
  const [importResults, setImportResults] = useState(null);

  const [importStatus, setImportStatus] = useState(IMPORT_STATUS.IDLE);
  const [errorMessage, setErrorMessage] = useState(null);
  const [ledgerNote, setLedgerNote] = useState(null);
  const [previewModalState, setPreviewModalState] = useState({ opened: false, resourceType: null });

  // The current CONNECT_IMPORT artifact (highest design-model version if multiple builds accumulated).
  const connectImportArtifact = useMemo(() => {
    const cands = (project.artifacts || []).filter((a) => a.type === 'CONNECT_IMPORT');
    return cands.sort((a, b) => (b.version || 0) - (a.version || 0))[0] || null;
  }, [project.artifacts]);

  const loadConnectInstances = useCallback(async () => {
    if (!awsCredentials) return;
    setLoadingInstances(true);
    setErrorMessage(null);
    try {
      const list = await listConnectInstances(awsCredentials, awsCredentials.region);
      setInstances(list || []);
    } catch (e) {
      console.error('Error loading instances:', e);
      setErrorMessage('Failed to load Connect instances. Please check your credentials.');
    } finally {
      setLoadingInstances(false);
    }
  }, [awsCredentials]);

  useEffect(() => { if (opened) loadConnectInstances(); }, [opened, loadConnectInstances]);

  const resetValidation = () => {
    setParsedData(null);
    setValidationResults(null);
    setValidationSummary(null);
    setExistingResources(null);
    setNameToIdMaps(null);
    setImportResults(null);
    setImportLog(null);
    setImportProgress({});
    setImportStatus(IMPORT_STATUS.IDLE);
    setLedgerNote(null);
  };

  const handleInstanceSelect = (instanceId) => {
    setSelectedInstance(instances.find((i) => i.Id === instanceId) || null);
    resetValidation();
    setErrorMessage(null);
  };

  // Fetch the CONNECT_IMPORT projection, then validate it against the target instance.
  const loadAndValidate = async () => {
    if (!selectedInstance) { setErrorMessage(ERROR_MESSAGES.INSTANCE_NOT_SELECTED); return; }
    if (!connectImportArtifact) { setErrorMessage('No Connect-import artifact found. Run a Build first.'); return; }

    setImportStatus(IMPORT_STATUS.VALIDATING);
    setErrorMessage(null);
    try {
      // 1. Read the projection (already shaped like the importer's parsedData) via its presigned URL.
      const { url } = await getArtifactUrl(project.projectId, connectImportArtifact.id, getApiToken);
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to read Connect-import artifact (${res.status})`);
      const projection = await res.json();
      const data = { ...(projection.resources || {}) };
      // Mirror AWSImportPage: give hierarchy rows their display name + parent ordering.
      if (Array.isArray(data.userHierarchies) && data.userHierarchies.length > 0) {
        data.userHierarchies = buildUserHierarchyStructure(data.userHierarchies);
      }
      setParsedData(data);

      // 2. Fetch existing resources + name→id maps from the instance.
      const existing = await listExistingResources(awsCredentials, awsCredentials.region, selectedInstance.Id);
      setExistingResources(existing);
      setNameToIdMaps(buildNameToIdMaps(existing));

      // 3. Validate (conflicts + dependency warnings).
      const validationService = new ImportValidationService(existing, data);
      const validation = validationService.validate();
      setValidationResults(validation.results);
      setValidationSummary(validationService.getSummary());

      // Default-select every importable type that has rows.
      const initialSelection = {};
      IMPORTABLE_RESOURCE_TYPES.forEach((type) => {
        initialSelection[type] = Array.isArray(data[type]) && data[type].length > 0;
      });
      setSelectedResourceTypes(initialSelection);

      setImportStatus(IMPORT_STATUS.READY);
    } catch (e) {
      console.error('Error validating Connect import:', e);
      setErrorMessage(e.message || ERROR_MESSAGES.VALIDATION_ERROR);
      setImportStatus(IMPORT_STATUS.ERROR);
    }
  };

  const handleResourceToggle = (resourceType) => {
    setSelectedResourceTypes((prev) => ({ ...prev, [resourceType]: !prev[resourceType] }));
  };

  const handlePerResourceConflictChange = (resourceType, resourceName, resolution) => {
    setPerResourceConflicts((prev) => ({
      ...prev,
      [resourceType]: { ...(prev[resourceType] || {}), [resourceName]: resolution },
    }));
  };

  const handleImport = async () => {
    if (!Object.values(selectedResourceTypes).some(Boolean)) { setErrorMessage(ERROR_MESSAGES.NO_RESOURCES_SELECTED); return; }
    if (!selectedInstance || !parsedData) { setErrorMessage(ERROR_MESSAGES.INSTANCE_NOT_SELECTED); return; }

    setImportStatus(IMPORT_STATUS.IMPORTING);
    setErrorMessage(null);
    setImportProgress({});
    setImportResults(null);

    const logService = new ImportLogService();
    logService.start();
    setImportLog(logService);

    try {
      const results = await importResources({
        parsedData,
        selectedResourceTypes,
        conflictResolution,
        perResourceConflicts,
        existingResources,
        nameToIdMaps,
        credentials: awsCredentials,
        region: awsCredentials.region,
        instanceId: selectedInstance.Id,
        onProgress: (u) => setImportProgress((prev) => ({
          ...prev,
          [u.resourceType]: { state: u.state, current: u.current, total: u.total, message: u.message, error: u.error },
        })),
        onLog: (resourceType, resourceName, action, details) => logService.logResourceAction(resourceType, resourceName, action, details),
      });
      logService.end();
      setImportResults(results);
      setImportStatus(IMPORT_STATUS.COMPLETE);

      // Record the real Connect resource IDs into the project's per-instance import ledger, for reuse
      // across imports + flow-reference resolution at export. Best-effort: never fail the import on this.
      try {
        const ledger = ledgerResults(results, nameToIdMaps);
        if (ledger.length > 0) {
          // Re-read the project so the guarded write uses the CURRENT version — the Connect import can
          // run for minutes, during which the version captured at mount (`project.version`) may be
          // stale (a concurrent edit), which would 409 the ledger write and lose the recorded IDs.
          let expectedVersion = project.version;
          try {
            const fresh = await getProject(project.projectId, getApiToken);
            if (fresh && typeof fresh.version === 'number') expectedVersion = fresh.version;
          } catch { /* fall back to the mount-time version */ }
          await recordImportResults(project.projectId, expectedVersion, {
            instanceId: selectedInstance.Id,
            instanceAlias: selectedInstance.InstanceAlias,
            region: awsCredentials.region,
            results: ledger,
          }, getApiToken);
          setLedgerNote(`Recorded ${ledger.length} resource ID(s) for ${selectedInstance.InstanceAlias || selectedInstance.Id}.`);
          if (onImported) onImported();
        }
      } catch (ledgerErr) {
        console.warn('Failed to record import ledger:', ledgerErr);
      }
    } catch (e) {
      console.error('Import failed:', e);
      setErrorMessage(e.message || ERROR_MESSAGES.IMPORT_ERROR);
      setImportStatus(IMPORT_STATUS.ERROR);
      logService.end();
    }
  };

  const getStatusBadge = (resourceType) => {
    const result = validationResults?.[resourceType];
    if (!result?.dependencyWarnings?.length) return null;
    return (
      <Tooltip label={`${result.dependencyWarnings.length} warnings`}>
        <Badge color="yellow" leftSection={<IconAlertCircle size={14} />}>Warnings</Badge>
      </Tooltip>
    );
  };

  const instanceOptions = instances.map((i) => ({ value: i.Id, label: i.InstanceAlias || i.Id }));
  const importing = importStatus === IMPORT_STATUS.IMPORTING;

  return (
    <Modal opened={opened} onClose={importing ? () => {} : onClose} size="xl" title={
      <Group gap="xs"><IconCloudUpload size={20} /><Title order={4}>Import to Connect</Title></Group>
    } closeOnClickOutside={!importing} closeOnEscape={!importing}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Push this project's Connect operational resources (hours, queues, routing profiles, agent
          status, quick connects, user hierarchies) directly into a target instance — the same pipeline
          as the AWS Import tool, fed from this project's Build artifacts.
        </Text>

        <AWSCredentialChangeSection
          awsCredentials={awsCredentials}
          setAwsCredentials={setAwsCredentials}
          onCredentialsUpdated={() => { setInstances([]); setSelectedInstance(null); resetValidation(); loadConnectInstances(); }}
        />

        {!awsCredentials && (
          <Alert icon={<IconAlertCircle size={16} />} title="AWS Credentials Required" color="orange">
            Configure your AWS credentials above to select a target instance.
          </Alert>
        )}

        {!connectImportArtifact && (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow">
            No Connect-import artifact yet — generate a Build first.
          </Alert>
        )}

        {errorMessage && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" withCloseButton onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}

        {/* Step 1 — instance + validate */}
        <Group align="flex-end" gap="md">
          <Select
            style={{ flex: 1 }}
            label="Target Connect instance"
            placeholder="Select instance"
            data={instanceOptions}
            value={selectedInstance?.Id || null}
            onChange={handleInstanceSelect}
            disabled={!awsCredentials || loadingInstances || importing}
            leftSection={loadingInstances ? <Loader size="xs" /> : null}
            searchable
          />
          <Button
            color="orange"
            onClick={loadAndValidate}
            disabled={!selectedInstance || !connectImportArtifact || importing}
            loading={importStatus === IMPORT_STATUS.VALIDATING}
          >
            Load &amp; validate
          </Button>
        </Group>

        {/* Step 2 — selection + run */}
        {importStatus !== IMPORT_STATUS.IDLE && importStatus !== IMPORT_STATUS.VALIDATING && validationResults && (
          <>
            <Divider />
            {validationSummary && (
              <Alert icon={<IconInfoCircle size={16} />} color="blue">
                <Group gap="xl">
                  <Text size="sm"><strong>Total:</strong> {validationSummary.totalResources}</Text>
                  <Text size="sm" c="green"><strong>New:</strong> {validationSummary.totalNew}</Text>
                  <Text size="sm" c="yellow"><strong>Existing:</strong> {validationSummary.totalExisting}</Text>
                  {validationSummary.totalWarnings > 0 && (
                    <Text size="sm" c="orange"><strong>Warnings:</strong> {validationSummary.totalWarnings}</Text>
                  )}
                </Group>
              </Alert>
            )}

            <div>
              <Text size="sm" fw={500} mb="xs">Conflict resolution:</Text>
              <Radio.Group value={conflictResolution} onChange={setConflictResolution}>
                <Group>
                  <Radio value={CONFLICT_RESOLUTION.SKIP} label="Skip existing resources" disabled={importing} />
                  <Radio value={CONFLICT_RESOLUTION.UPDATE} label="Update existing resources" disabled={importing} />
                </Group>
              </Radio.Group>
            </div>

            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Import</Table.Th><Table.Th>Resource Type</Table.Th><Table.Th>Total</Table.Th>
                  <Table.Th>New</Table.Th><Table.Th>Existing</Table.Th><Table.Th>Status</Table.Th><Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {IMPORTABLE_RESOURCE_TYPES.map((resourceType) => {
                  const result = validationResults[resourceType];
                  if (!result || result.total === 0) return null;
                  return (
                    <Table.Tr key={resourceType}>
                      <Table.Td>
                        <Checkbox checked={selectedResourceTypes[resourceType] || false}
                          onChange={() => handleResourceToggle(resourceType)} disabled={importing} />
                      </Table.Td>
                      <Table.Td><Text fw={500}>{RESOURCE_TYPE_LABELS[resourceType]}</Text></Table.Td>
                      <Table.Td>{result.total}</Table.Td>
                      <Table.Td><Badge color="green" variant="light">{result.new}</Badge></Table.Td>
                      <Table.Td><Badge color="yellow" variant="light">{result.existing}</Badge></Table.Td>
                      <Table.Td>{getStatusBadge(resourceType)}</Table.Td>
                      <Table.Td>
                        <Button size="xs" variant="light" leftSection={<IconEye size={14} />}
                          onClick={() => setPreviewModalState({ opened: true, resourceType })}>
                          View Details
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>

            <Text size="xs" c="dimmed">
              Security profiles and contact flows are projected but not yet imported by this tool
              (contact flows are available as the Build tab .zip download).
            </Text>

            <Group justify="flex-end">
              <Button leftSection={<IconUpload size={18} />} color="orange" onClick={handleImport}
                disabled={!Object.values(selectedResourceTypes).some(Boolean) || importing} loading={importing}>
                {importing ? 'Importing…' : 'Run Import'}
              </Button>
            </Group>
          </>
        )}

        {importing && <ImportProgressPanel importProgress={importProgress} importStatus={importStatus} />}

        {importStatus === IMPORT_STATUS.COMPLETE && importResults && (
          <Alert icon={<IconCheck size={16} />} color="green">
            <Group gap="xl">
              <Text size="sm"><strong>Created:</strong> {importResults.success.length}</Text>
              <Text size="sm"><strong>Skipped:</strong> {importResults.skipped.length}</Text>
              <Text size="sm"><strong>Failed:</strong> {importResults.failed.length}</Text>
            </Group>
            {ledgerNote && <Text size="xs" c="dimmed" mt={4}>{ledgerNote} These IDs are reused on re-import and resolve flow references at export.</Text>}
          </Alert>
        )}

        {importStatus === IMPORT_STATUS.COMPLETE && importLog && (
          <ImportLogViewer importLog={importLog} importResults={importResults} />
        )}
      </Stack>

      <ImportPreviewModal
        opened={previewModalState.opened}
        onClose={() => setPreviewModalState({ opened: false, resourceType: null })}
        resourceType={previewModalState.resourceType}
        validationResults={validationResults}
        parsedData={parsedData}
        globalConflictResolution={conflictResolution}
        perResourceConflicts={perResourceConflicts}
        onConflictResolutionChange={handlePerResourceConflictChange}
      />
    </Modal>
  );
}

export default SolnStudioImportModal;
